/**
 * Kredi kartı satışı faturası.
 *
 * Fatura kesme kuralı ödeme kanalına göre:
 *   - Havale / EFT : tutarın tamamı her zaman faturalanır (banka hareketi eşleşmesi)
 *   - Nakit        : faturalanmaz
 *   - Kredi kartı  : KART TUTARI eşiği (36.000 TL) aşıyorsa faturalanır
 *
 * Kart satışının tek tek banka hareketi yok (POS günsonu toplu yatar), bu yüzden
 * taslak doğrudan işlemden açılır. Satış ürünü ne olursa olsun fatura 22 Ayar
 * Bilezik olarak kesilir: fiyata POS komisyonu eklendiği için 24 ayar birim
 * fiyatı tutmuyor (mağazanın Kolaysoft'taki uygulaması da bu).
 *
 * Karma ödemede yalnızca kart kısmı faturalanır; havale kısmı kendi banka
 * hareketinden ayrı taslak üretir, aynı para iki kez faturalanmaz.
 */

import { getIstanbulDayKey } from './bankingDateTime';

/** Kural yürürlüğe girdiği gün — öncesindeki satışlar Kolaysoft'tan kesildi. */
export const KREDI_KARTI_RULE_START_DATE = '2026-09-16';

export const DEFAULT_KREDI_KARTI_RULE = {
  enabled: true,
  /** Kart tutarı bu değeri AŞARSA fatura kesilir */
  thresholdTl: 36000,
  /** Bu günden (dahil) sonraki satışlar değerlendirilir — YYYY-MM-DD */
  startDate: KREDI_KARTI_RULE_START_DATE,
  /** Faturadaki ürün tipi (INVOICE_PRODUCT_TYPE_OPTIONS değeri) */
  invoiceAs: '22bilezik'
};

export const KREDI_KARTI_MATCH_SOURCE = 'krediKarti';

const isDayKey = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

export const mergeKrediKartiRule = (raw) => {
  const base = DEFAULT_KREDI_KARTI_RULE;
  if (raw == null) return { ...base };
  const threshold = Number(raw.thresholdTl);
  return {
    enabled: raw.enabled !== false,
    thresholdTl: Number.isFinite(threshold) && threshold > 0 ? threshold : base.thresholdTl,
    startDate: isDayKey(raw.startDate) ? String(raw.startDate) : base.startDate,
    invoiceAs: String(raw.invoiceAs || base.invoiceAs)
  };
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const getKrediKartiAmount = (islem) => round2(Number(islem?.krediKarti) || 0);

/** İşlemin İstanbul takvim günü (YYYY-MM-DD) */
export const getIslemDayKey = (islem) =>
  getIstanbulDayKey(islem?.timestamp || islem?.tarih || islem?.createdAt) || '';

/**
 * Aynı seansta girilmiş çok parçalı işlemin grup anahtarı.
 * Tek parçalı işlemlerde kendi id'si döner.
 */
export const getIslemGrupAnahtari = (islem) =>
  islem?.isMulti && islem?.groupId ? `grup:${islem.groupId}` : `tek:${String(islem?.id || '')}`;

/**
 * Kart satışlarını çok parçalı işleme göre gruplar.
 *
 * NEDEN: yazarkasa 36.000 TL'ye kadar fiş kesiyor; üstünde yalnız kart tahsilatı
 * yapılıp fatura buradan kesiliyor. Eşik PARÇA başına değil, aynı seansta
 * girilen işlemin TAMAMINA bakmalı. Örnek (BERK AKAY, 0994701): 45.360 + 11.340
 * = 56.700. Parça bazında bakılınca 11.340 eşiğin altında kalıyor ve faturaya
 * hiç girmiyordu; fatura 56.700 yerine 45.360 çıkıyordu.
 *
 * @returns {Array<{ anahtar: string, islemler: object[], toplamKart: number }>}
 */
export const groupKrediKartiIslemler = (islemler = []) => {
  const gruplar = new Map();
  for (const islem of islemler || []) {
    if (!islem?.id) continue;
    if (String(islem.islemTuru || '').trim() !== 'Satış') continue;
    if (!(getKrediKartiAmount(islem) > 0)) continue;
    const anahtar = getIslemGrupAnahtari(islem);
    if (!gruplar.has(anahtar)) gruplar.set(anahtar, []);
    gruplar.get(anahtar).push(islem);
  }
  return [...gruplar.entries()].map(([anahtar, liste]) => ({
    anahtar,
    // Parça sırası korunur: 0994701/1, /2 …
    islemler: [...liste].sort((a, b) => String(a.islemNo || '').localeCompare(String(b.islemNo || ''))),
    toplamKart: round2(liste.reduce((t, x) => t + getKrediKartiAmount(x), 0))
  }));
};

/**
 * Grup fatura kesilmeli mi? Eşik grubun KART TOPLAMINA uygulanır.
 * @returns {{ ok: boolean, reason?: string, toplamKart: number }}
 */
export const evaluateKrediKartiGroup = (grup, ruleRaw = null) => {
  const rule = mergeKrediKartiRule(ruleRaw);
  const toplamKart = round2(Number(grup?.toplamKart) || 0);
  if (!rule.enabled) return { ok: false, reason: 'kapali', toplamKart };
  if (!grup?.islemler?.length) return { ok: false, reason: 'islem-yok', toplamKart };
  if (!(toplamKart > rule.thresholdTl)) return { ok: false, reason: 'esik-alti', toplamKart };
  // Grubun günü: parçalar aynı seansta girildiği için ilk parçanın günü yeterli.
  const day = getIslemDayKey(grup.islemler[0]);
  if (!day) return { ok: false, reason: 'tarih-yok', toplamKart };
  if (day < rule.startDate) return { ok: false, reason: 'baslangic-oncesi', toplamKart };
  return { ok: true, toplamKart };
};

/**
 * Bu işlem için kredi kartı faturası kesilmeli mi?
 * @returns {{ ok: boolean, reason?: string }}
 */
export const evaluateKrediKartiCandidate = (islem, ruleRaw = null) => {
  const rule = mergeKrediKartiRule(ruleRaw);
  if (!rule.enabled) return { ok: false, reason: 'kapali' };
  if (!islem?.id) return { ok: false, reason: 'islem-yok' };
  if (String(islem.islemTuru || '').trim() !== 'Satış') return { ok: false, reason: 'satis-degil' };
  const kk = getKrediKartiAmount(islem);
  if (!(kk > rule.thresholdTl)) return { ok: false, reason: 'esik-alti' };
  const day = getIslemDayKey(islem);
  if (!day) return { ok: false, reason: 'tarih-yok' };
  if (day < rule.startDate) return { ok: false, reason: 'baslangic-oncesi' };
  return { ok: true };
};

/**
 * Faturalanacak işlem kopyası: tutar = kart kısmı, ürün tipi = 22 Ayar Bilezik.
 *
 * Taslak ekranı kalemleri islemData'dan yeniden ürettiği için bu kopya kayda
 * islemData olarak yazılır; her açılışta aynı sonuç çıkar. Orijinal ödeme
 * dağılımı `krediKartiKaynak` altında saklanır.
 */
/**
 * @param {object} islem
 * @param {object} ruleRaw  eski kredi kartı kuralı (yalnız eşik/keşif için)
 * @param {{ sartVar?: boolean }} [opts]
 *   sartVar: fatura şartları (settings.invoiceRules) tanımlıysa ÜRÜN TİPİNE
 *   burada karar verilmez; kararı şart listesi verir. Eskiden bu fonksiyon
 *   ürünü zorla "22 Ayar Bilezik" yapıyordu ve şartlar hiç sıra alamıyordu.
 */
export const buildKrediKartiInvoiceIslem = (islem, ruleRaw = null, opts = null) => {
  const rule = mergeKrediKartiRule(ruleRaw);
  const sartVar = Boolean(opts?.sartVar);
  const kk = getKrediKartiAmount(islem);
  const toplam = round2(Number(islem?.toplamTutar) || 0);
  const karma = toplam - kk > 1;
  return {
    ...islem,
    // Kalem hesabı havaleEft/toplamTutar'dan okuduğu için kart tutarına eşitlenir.
    // toplamTutar da eşitlenir ki "kısmi ödeme" şartları bu taslakta devreye girmesin.
    havaleEft: kk,
    toplamTutar: kk,
    nakit: 0,
    krediKarti: kk,
    // Karma ödemede işlemin gramı satışın tamamına ait; kart kısmının gramı tutardan hesaplanır.
    gram: karma ? 0 : Number(islem?.gram) || 0,
    ...(sartVar ? {} : { invoiceProductTypeOverride: rule.invoiceAs }),
    invoicePaymentChannel: KREDI_KARTI_MATCH_SOURCE,
    krediKartiKaynak: {
      toplamTutar: toplam,
      krediKarti: kk,
      nakit: round2(Number(islem?.nakit) || 0),
      havaleEft: round2(Number(islem?.havaleEft) || 0),
      gram: Number(islem?.gram) || 0,
      karmaOdeme: karma
    }
  };
};

/** Aynı işlem (ya da grup) için tek taslak — belge kimliği deterministik. */
export const krediKartiMatchDocId = (islemId) => `krediKarti__${String(islemId || '').replace(/\//g, '_')}`;

/** Grup taslağının belge kimliği: ilk parçanın id'si üzerinden, deterministik. */
export const krediKartiGrupDocId = (islemler = []) => {
  const ilk = [...(islemler || [])].sort((a, b) =>
    String(a.islemNo || a.id || '').localeCompare(String(b.islemNo || b.id || ''))
  )[0];
  return krediKartiMatchDocId(ilk?.id || '');
};

/** Kayıt kredi kartı faturası mı */
export const isKrediKartiMatch = (match) =>
  match?.invoiceSource === KREDI_KARTI_MATCH_SOURCE ||
  String(match?.id || '').startsWith('krediKarti__');
